import React from 'react';

// ─── Button ────────────────────────────────────────────────────────────────
export function Button({ label, onClick, disabled, loading, variant = 'primary', style = {}, icon, fullWidth }) {
    const base = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '10px 20px', borderRadius: 'var(--radius-md)', fontSize: 15,
        fontWeight: 600, fontFamily: 'var(--font-body)', cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.55 : 1, transition: 'all 0.15s', border: 'none',
        width: fullWidth ? '100%' : 'auto', whiteSpace: 'nowrap',
        ...style,
    };
    const variants = {
        primary: { backgroundColor: 'var(--brand-500)', color: 'var(--white)' },
        outline: { backgroundColor: 'transparent', color: 'var(--brand-300)', border: '1px solid var(--brand-500)' },
        danger: { backgroundColor: 'transparent', color: '#fca5a5', border: '1px solid #dc2626' },
        ghost: { backgroundColor: 'transparent', color: 'var(--brand-300)' },
    };
    return (
        <button onClick={onClick} disabled={disabled || loading} style={{ ...base, ...variants[variant] }}>
            {loading
                ? <Spinner size={16} color={variant === 'primary' ? '#fff' : 'var(--brand-300)'} />
                : <>{icon && <span>{icon}</span>}<span>{label}</span></>
            }
        </button>
    );
}

// ─── Card ──────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, onClick }) {
    const base = {
        backgroundColor: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
        padding: 16, marginBottom: 12, border: '1px solid var(--border)',
        cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s',
        ...style,
    };
    return (
        <div style={base} onClick={onClick}
            onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = 'var(--brand-500)')}
            onMouseLeave={e => onClick && (e.currentTarget.style.borderColor = 'var(--border)')}>
            {children}
        </div>
    );
}

// ─── Badge ─────────────────────────────────────────────────────────────────
export function Badge({ label, color, bgColor, borderColor }) {
    return (
        <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 'var(--radius-full)',
            backgroundColor: bgColor || 'var(--bg-surface)', color: color || 'var(--brand-300)',
            fontSize: 11, fontWeight: 700, border: `1px solid ${borderColor || 'transparent'}`,
        }}>{label}</span>
    );
}

// ─── Input ─────────────────────────────────────────────────────────────────
export function Input({ label, style = {}, inputStyle = {}, ...props }) {
    return (
        <div style={{ marginBottom: 12, ...style }}>
            {label && <label style={{
                display: 'block', color: 'var(--text-muted)', fontSize: 11,
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4
            }}>
                {label}
            </label>}
            <input style={{
                display: 'block', width: '100%', backgroundColor: 'var(--bg-input)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                padding: '10px 14px', color: 'var(--text-primary)', fontSize: 15,
                ...inputStyle,
            }} {...props} />
        </div>
    );
}

// ─── Textarea ──────────────────────────────────────────────────────────────
export function Textarea({ label, style = {}, ...props }) {
    return (
        <div style={{ marginBottom: 12, ...style }}>
            {label && <label style={{
                display: 'block', color: 'var(--text-muted)', fontSize: 11,
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4
            }}>
                {label}
            </label>}
            <textarea style={{
                display: 'block', width: '100%', backgroundColor: 'var(--bg-input)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                padding: '10px 14px', color: 'var(--text-primary)', fontSize: 15,
                resize: 'vertical', minHeight: 80, fontFamily: 'var(--font-body)',
            }} {...props} />
        </div>
    );
}

// ─── SectionHeader ─────────────────────────────────────────────────────────
export function SectionHeader({ title, action, onAction }) {
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 8, marginTop: 16
        }}>
            <span style={{
                color: 'var(--text-primary)', fontSize: 17, fontWeight: 700,
                fontFamily: 'var(--font-display)'
            }}>{title}</span>
            {action && <button onClick={onAction} style={{
                background: 'none', border: 'none',
                color: 'var(--brand-300)', fontSize: 13, cursor: 'pointer'
            }}>{action}</button>}
        </div>
    );
}

// ─── Spinner ───────────────────────────────────────────────────────────────
export function Spinner({ size = 20, color = 'var(--brand-400)' }) {
    return (
        <div style={{
            width: size, height: size, border: `2px solid transparent`,
            borderTopColor: color, borderRadius: '50%',
            animation: 'spin 0.7s linear infinite', flexShrink: 0,
        }} />
    );
}

// ─── LoadingView ───────────────────────────────────────────────────────────
export function LoadingView({ label }) {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: 48, gap: 12, flex: 1
        }}>
            <Spinner size={32} />
            {label && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{label}</p>}
        </div>
    );
}

// ─── ErrorView ─────────────────────────────────────────────────────────────
export function ErrorView({ message, onRetry }) {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: 48, gap: 12, textAlign: 'center'
        }}>
            <span style={{ fontSize: 40 }}>⚠️</span>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{message}</p>
            {onRetry && <Button label="Retry" onClick={onRetry} />}
        </div>
    );
}

// ─── EmptyView ─────────────────────────────────────────────────────────────
export function EmptyView({ emoji = '📭', title, subtitle, action, onAction }) {
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: 48, gap: 8, textAlign: 'center'
        }}>
            <span style={{ fontSize: 48 }}>{emoji}</span>
            <p style={{ color: 'var(--text-secondary)', fontSize: 17, fontWeight: 600 }}>{title}</p>
            {subtitle && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{subtitle}</p>}
            {action && <Button label={action} onClick={onAction} style={{ marginTop: 8 }} />}
        </div>
    );
}

// ─── Divider ───────────────────────────────────────────────────────────────
export function Divider({ style = {} }) {
    return <div style={{ height: 1, backgroundColor: 'var(--border)', margin: '16px 0', ...style }} />;
}

// ─── Toast ─────────────────────────────────────────────────────────────────
let toastSetters = [];
export function registerToast(setter) { toastSetters.push(setter); }
export function unregisterToast(setter) { toastSetters = toastSetters.filter(s => s !== setter); }
export const Toast = {
    show: ({ type = 'info', text1, text2 }) => {
        toastSetters.forEach(s => s({ type, text1, text2, id: Date.now() }));
    }
};

export function ToastContainer() {
    const [toasts, setToasts] = React.useState([]);
    React.useEffect(() => {
        const setter = (t) => {
            setToasts(prev => [...prev, t]);
            setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), 3500);
        };
        registerToast(setter);
        return () => unregisterToast(setter);
    }, []);

    const typeColors = { success: '#4ade80', error: '#f87171', info: '#60a5fa', warning: '#fbbf24' };

    return (
        <div style={{
            position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex',
            flexDirection: 'column', gap: 8, pointerEvents: 'none'
        }}>
            {toasts.map(t => (
                <div key={t.id} style={{
                    backgroundColor: 'var(--bg-card)', border: `1px solid ${typeColors[t.type] || 'var(--border)'}`,
                    borderRadius: 'var(--radius-md)', padding: '12px 16px', minWidth: 240, maxWidth: 320,
                    animation: 'slideUp 0.3s ease', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}>
                    <p style={{ color: typeColors[t.type] || 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>{t.text1}</p>
                    {t.text2 && <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{t.text2}</p>}
                </div>
            ))}
        </div>
    );
}

// ─── Modal ─────────────────────────────────────────────────────────────────
export function Modal({ visible, onClose, children, title }) {
    if (!visible) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
            alignItems: 'flex-end', justifyContent: 'center',
            backgroundColor: 'rgba(5,46,22,0.85)', padding: 0,
        }} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{
                backgroundColor: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
                width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto',
                padding: 24, border: '1px solid var(--border)',
                animation: 'slideUp 0.3s ease',
            }}>
                {title && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>{title}</h2>
                        <button onClick={onClose} style={{
                            background: 'none', border: 'none',
                            color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer', lineHeight: 1
                        }}>✕</button>
                    </div>
                )}
                {children}
            </div>
        </div>
    );
}

// ─── FieldLabel ────────────────────────────────────────────────────────────
export function FieldLabel({ children }) {
    return (
        <label style={{
            display: 'block', color: 'var(--text-muted)', fontSize: 11,
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
            marginBottom: 4, marginTop: 12
        }}>
            {children}
        </label>
    );
}

export function FieldInput({ style = {}, ...props }) {
    return (
        <input style={{
            display: 'block', width: '100%', backgroundColor: 'var(--bg-input)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
            padding: '10px 14px', color: 'var(--text-primary)', fontSize: 15,
            fontFamily: 'var(--font-body)', ...style,
        }} {...props} />
    );
}
